import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useToast } from '@chakra-ui/react'
import { useDispatch, useSelector } from 'react-redux'

import {
  addContactMethodRequest,
  updateContactMethodRequest,
  deleteContactMethodRequest,
  loadContactMethods,
} from '../redux/features/contactMethodsSlice'

const getErrorMessage = (error, defaultMsg) => {
  if (error && error.name) return error.name[0] || defaultMsg
  if (typeof error === 'string') return error
  return defaultMsg
}

const ContactMethodsContext = createContext()

export const ContactMethodsContextProvider = ({ children }) => {
  const dispatch = useDispatch()
  const toast = useToast()

  const { data = {}, loading = false } = useSelector(
    state => state.contactmethods
  )
  const contactMethods = Array.isArray(data.results) ? data.results : []

  const [showActiveOnly, setShowActiveOnly] = useState(true)

  const [isAddOpen, setIsAddOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [modalActive, setModalActive] = useState(true)

  const [editId, setEditId] = useState(null)
  const [editName, setEditName] = useState('')
  const [editActive, setEditActive] = useState(true)

  const [deleteId, setDeleteId] = useState(null)

  const visibleContactMethods = useMemo(() => {
    return showActiveOnly
      ? contactMethods.filter(cm => cm.is_active)
      : contactMethods
  }, [contactMethods, showActiveOnly])

  useEffect(() => {
    dispatch(loadContactMethods())
  }, [dispatch])

  const openAdd = useCallback(() => setIsAddOpen(true), [])
  const closeAdd = useCallback(() => {
    setNewName('')
    setModalActive(true)
    setIsAddOpen(false)
  }, [])

  const handleAdd = useCallback(() => {
    if (!newName.trim()) return
    dispatch(
      addContactMethodRequest({
        contactMethod: { name: newName, is_active: modalActive },
        callbackSuccess: () => {
          toast({ status: 'success', title: 'Contact method added' })
          closeAdd()
        },
        callbackFailure: error => {
          const message = getErrorMessage(error, 'Failed to add contact method')
          toast({ status: 'error', title: message })
        },
      })
    )
  }, [dispatch, toast, newName, modalActive, closeAdd])

  const handleEdit = useCallback(cm => {
    setEditId(cm.id)
    setEditName(cm.name)
    setEditActive(cm.is_active)
  }, [])

  const handleSave = useCallback(
    id => {
      dispatch(
        updateContactMethodRequest({
          contactMethod: { id, name: editName, is_active: editActive },
          callbackSuccess: () => {
            toast({ status: 'success', title: 'Contact method updated' })
            setEditId(null)
          },
          callbackFailure: error => {
            const message = getErrorMessage(
              error,
              'Failed to update contact method'
            )
            toast({ status: 'error', title: message })
          },
        })
      )
    },
    [dispatch, toast, editName, editActive]
  )

  const handleCancel = useCallback(() => setEditId(null), [])

  const handleDelete = useCallback(id => setDeleteId(id), [])
  const closeDeleteDialog = useCallback(() => setDeleteId(null), [])

  const confirmDelete = useCallback(() => {
    if (!deleteId) return
    dispatch(
      deleteContactMethodRequest({
        id: deleteId,
        callbackSuccess: () => {
          toast({ status: 'success', title: 'Contact method deleted' })
          closeDeleteDialog()
        },
        callbackFailure: error => {
          const message = getErrorMessage(
            error,
            'Failed to delete contact method'
          )
          toast({ status: 'error', title: message })
          closeDeleteDialog()
        },
      })
    )
  }, [dispatch, toast, deleteId, closeDeleteDialog])

  const deleteContactMethod = useMemo(
    () => contactMethods.find(cm => cm.id === deleteId),
    [contactMethods, deleteId]
  )

  const value = {
    contactMethods,
    visibleContactMethods,
    loading,

    showActiveOnly,
    setShowActiveOnly,

    isAddOpen,
    openAdd,
    closeAdd,
    newName,
    setNewName,
    modalActive,
    setModalActive,
    handleAdd,

    editId,
    editName,
    setEditName,
    editActive,
    setEditActive,
    handleEdit,
    handleSave,
    handleCancel,

    deleteId,
    deleteContactMethod,
    handleDelete,
    confirmDelete,
    closeDeleteDialog,
  }

  return (
    <ContactMethodsContext.Provider value={value}>
      {children}
    </ContactMethodsContext.Provider>
  )
}

export const useContactMethodsContext = () => useContext(ContactMethodsContext)

export default ContactMethodsContext
